'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import Link from 'next/link'

interface MobileStickyCTAProps {
  href: string
  text: string
  ariaLabel: string
  dataCtaLocation: string
  heroSelector?: string
  formSelector?: string
}

export default function MobileStickyCTA({
  href,
  text,
  ariaLabel,
  dataCtaLocation,
  heroSelector = '[data-hero-cta]',
  formSelector
}: MobileStickyCTAProps) {
  const [isVisible, setIsVisible] = useState(false)
  const heroInViewRef = useRef(false)
  const formInViewRef = useRef(false)
  const heroObserverRef = useRef<IntersectionObserver | null>(null)
  const formObserverRef = useRef<IntersectionObserver | null>(null)
  const rafRef = useRef<number | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Debounced visibility update with RAF for smooth performance
  const updateVisibility = useCallback(() => {
    // Cancel any pending updates
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Use RAF for smooth updates during scrolling
    rafRef.current = requestAnimationFrame(() => {
      const shouldBeVisible = !heroInViewRef.current && !formInViewRef.current
      
      // Add small delay to prevent flickering during rapid scroll changes
      timeoutRef.current = setTimeout(() => {
        setIsVisible(shouldBeVisible)
      }, 50)
    })
  }, [])

  useEffect(() => {
    // Only show on mobile
    const checkMobile = () => window.innerWidth < 768
    
    if (!checkMobile()) {
      setIsVisible(false)
      return
    }

    // Find the hero CTA element
    const heroElement = document.querySelector(heroSelector)
    
    if (heroElement) {
      // Create intersection observer to track hero CTA visibility
      heroObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            heroInViewRef.current = entry.isIntersecting
            updateVisibility()
          })
        },
        {
          threshold: [0, 0.1],
          rootMargin: '-80px 0px 0px 0px'
        }
      )

      heroObserverRef.current.observe(heroElement)
    }

    // Find and observe the form section if selector provided
    if (formSelector) {
      const formElement = document.querySelector(formSelector)
      
      if (formElement) {
        formObserverRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              formInViewRef.current = entry.isIntersecting
              updateVisibility()
            })
          },
          {
            threshold: [0, 0.1],
            rootMargin: '0px 0px -80px 0px'
          }
        )

        formObserverRef.current.observe(formElement)
      }
    }

    // Handle window resize with debouncing
    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        if (!checkMobile()) {
          setIsVisible(false)
        }
      }, 100)
    }

    window.addEventListener('resize', handleResize, { passive: true })

    return () => {
      if (heroObserverRef.current) {
        heroObserverRef.current.disconnect()
      }
      if (formObserverRef.current) {
        formObserverRef.current.disconnect()
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [heroSelector, formSelector, updateVisibility])

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-md shadow-[0_-8px_24px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-out"
      style={{
        paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
        paddingTop: '0.75rem',
        transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
      aria-hidden={!isVisible}
    >
      <div className="px-4">
        <Link
          href={href}
          className="block w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-white bg-[#0A2342] hover:bg-[#12325c] active:bg-[#081a33] focus:outline-none focus:ring-2 focus:ring-white/60 shadow-sm transition-colors min-h-[44px] font-semibold text-center"
          aria-label={ariaLabel}
          data-cta={href.includes('mro') ? 'mro' : 'leasing'}
          data-cta-location={dataCtaLocation}
          tabIndex={isVisible ? 0 : -1}
        >
          {text}
        </Link>
      </div>
    </div>
  )
}

